var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
/* eslint-disable max-lines */
import { GEMINI_USAGE_CATEGORIES, GeminiUsageCategoryEnum } from '@workalizer/shared/enums';
import { GEMINI_USAGE_LEVELS } from '@workalizer/shared/lib/gemini-usage-level';
import { diffComparisonType } from '@workalizer/shared/types';
import { formatDuration, getPercentageDiff } from '@workalizer/shared/utils';
import { format, parseISO } from 'date-fns';
import i18n from 'i18next';
var emptyTimeSpentOnActivitiesPeriod = {
    period: '',
    docsCommentsPosted: 0,
    meetingsOrganized: 0,
    chatMessagesSent: 0,
    emailsSent: 0
};
export var processChartData = function (data, categories) {
    return data.map(function (entry) {
        var activity = categories
            .map(function (category) { return "".concat(category, ": ").concat(entry[category] || 0); })
            .join(', ');
        return "".concat(entry.name, ": ").concat(activity);
    });
};
export var processUserActivitySummary = function (data) {
    return data.map(function (entry) {
        var activity = "GMAIL: ".concat(entry.GMAIL, ", DRIVE: ").concat(entry.DRIVE, ", MEET: ").concat(entry.MEET, ", CALENDAR: ").concat(entry.CALENDAR, ", CHAT: ").concat(entry.CHAT, ", GEMINI: ").concat(entry.GEMINI);
        return "".concat(entry.name, ": ").concat(activity);
    });
};
export var processDocumentReportData = function (data) {
    return data.map(function (item) {
        return "Document Type: ".concat(item.type, ", Count: ").concat(item.count, ", Created: ").concat(item.created, ", Edited: ").concat(item.edited, ", Viewed: ").concat(item.viewed, ", Comments Updated: ").concat(item.commentsUpdated, ", Suggestions Updated: ").concat(item.suggestionsUpdated);
    });
};
export var processTimelineData = function (data) {
    return data.map(function (item) {
        var startTime = format(item.startDate, 'HH:mm');
        var endTime = item.endDate ? format(item.endDate, 'HH:mm') : null;
        return "".concat(item.title, ", Type: ").concat(item.type, ", ").concat(startTime).concat(endTime ? " - ".concat(endTime) : '');
    });
};
export var processHeatmapData = function (data) {
    return data.map(function (item) {
        return "Date: ".concat(item.date, ", Activity Count: ").concat(item.count);
    });
};
export var processUserDriveActivity = function (data) {
    var activityTypes = [
        'edited',
        'viewed',
        'commented',
        'suggested',
        'other'
    ];
    return activityTypes.flatMap(function (type) {
        return data[type].map(function (entry) {
            return "".concat(type.toUpperCase(), ": Document \"").concat(entry.title, "\" (").concat(entry.type, ") was ").concat(type, " ").concat(entry.actionCount, " times").concat(entry.action ? " (".concat(entry.action, ")") : '');
        });
    });
};
export var processMeetingsTypesData = function (data) {
    return data.map(function (item) {
        var countTeamDiff = item.count.teamAverage
            ? ", Team: ".concat(item.count.teamAverage.diffString)
            : '';
        var durationTeamDiff = item.duration.teamAverage
            ? ", Team: ".concat(item.duration.teamAverage.diffString)
            : '';
        return "\n      Title: ".concat(i18n.t("".concat(item.title)), "\n      Count: ").concat(item.count.value, " (Diff: ").concat(item.count.diff.diffString).concat(countTeamDiff, ")\n      Duration: ").concat(item.duration.value, " (Diff: ").concat(item.duration.diff.diffString).concat(durationTeamDiff, ")\n    ");
    });
};
export var processDocumentAlertsData = function (data) {
    return data.data.map(function (alert) {
        var date = alert.date, event = alert.event, document = alert.document, triggeredBy = alert.triggeredBy;
        return "On ".concat(date, ", document \"").concat(document.title, "\" was triggered by ").concat(triggeredBy.name || 'Unknown', " (").concat(triggeredBy.email, ") with event ").concat(event, ".");
    });
};
export var processEventsData = function (data) {
    return data.data.map(function (event) {
        var _a, _b, _c, _d;
        var date = event.date, action = event.action, title = event.title, user = event.user, meetingsEvent = event.meetingsEvent, documentEvent = event.documentEvent, chatEvent = event.chatEvent;
        var eventDetails = "Date: ".concat(format(parseISO(date), 'dd.MM.yyyy'));
        if (action) {
            eventDetails += ", Action: ".concat(action);
        }
        if (title) {
            eventDetails += ", Title: ".concat(title);
        }
        if ((user === null || user === void 0 ? void 0 : user.name) || (user === null || user === void 0 ? void 0 : user.email)) {
            eventDetails += ", User: ".concat(user.name || user.email);
        }
        if (meetingsEvent && ((_b = (_a = meetingsEvent.meeting) === null || _a === void 0 ? void 0 : _a.calendar) === null || _b === void 0 ? void 0 : _b.title)) {
            eventDetails += ", Meeting: ".concat(meetingsEvent.meeting.calendar.title);
        }
        if (documentEvent && ((_c = documentEvent.document) === null || _c === void 0 ? void 0 : _c.title)) {
            eventDetails += ", Document: ".concat(documentEvent.document.title);
        }
        if (chatEvent && ((_d = chatEvent.room) === null || _d === void 0 ? void 0 : _d.title)) {
            eventDetails += ", Chat Room: ".concat(chatEvent.room.title);
        }
        return eventDetails;
    });
};
export var processDocumentPeopleActivity = function (data) {
    var users = data.users, totals = data.totals;
    var totalStats = "Total Activity: ".concat(totals.views, " views, ").concat(totals.edits, " edits, ").concat(totals.downloads, " downloads, ").concat(totals.prints, " prints, ").concat(totals.commentsPosted, " comments posted, ").concat(totals.commentsResolved, " comments resolved, ").concat(totals.suggestionsPosted, " suggestions posted, ").concat(totals.suggestionsResolved, " suggestions resolved, ").concat(totals.sharedInternally, " shared internally, ").concat(totals.sharedExternally, " shared externally, ").concat(totals.sharedPublicly, " shared publicly");
    var userStats = users.map(function (user) {
        return "".concat(user.name || user.email).concat(user.orgUnitTitle ? " (".concat(user.orgUnitTitle, ")") : '', ": ").concat(user.views, " views, ").concat(user.edits, " edits, ").concat(user.downloads, " downloads, ").concat(user.prints, " prints, ").concat(user.commentsPosted, " comments posted, ").concat(user.commentsResolved, " comments resolved, ").concat(user.suggestionsPosted, " suggestions posted, ").concat(user.suggestionsResolved, " suggestions resolved, ").concat(user.sharedInternally, " shared internally, ").concat(user.sharedExternally, " shared externally, ").concat(user.sharedPublicly, " shared publicly");
    });
    return __spreadArray([totalStats], userStats, true);
};
export var processDocumentFileSummaryData = function (data) { return [
    "Views: ".concat(data.viewsEdits.left.count, " (").concat(data.viewsEdits.left.previous.diffString, "), Edits: ").concat(data.viewsEdits.right.count, " (").concat(data.viewsEdits.right.previous.diffString, ")"),
    "Comments: ".concat(data.commentsSuggestions.left.count, " (").concat(data.commentsSuggestions.left.previous.diffString, "), Suggestions: ").concat(data.commentsSuggestions.right.count, " (").concat(data.commentsSuggestions.right.previous.diffString, ")"),
    "Downloads: ".concat(data.downloadsPrints.left.count, " (").concat(data.downloadsPrints.left.previous.diffString, "), Prints: ").concat(data.downloadsPrints.right.count, " (").concat(data.downloadsPrints.right.previous.diffString, ")"),
    "Sharing internal: ".concat(data.sharing.internal.count, " (").concat(data.sharing.internal.previous.diffString, "), external: ").concat(data.sharing.external.count, " (").concat(data.sharing.external.previous.diffString, ")"),
    "Collaborators: ".concat(data.collaborators.count, " (").concat(data.collaborators.previous.diffString, ")")
]; };
export var processMeetUsersTimelineData = function (data) {
    var allEvents = data
        .flatMap(function (userData) {
        return userData.timelineData.map(function (event) { return ({
            time: new Date(event.time),
            user: userData.user.name || userData.user.email,
            type: event.type
        }); });
    })
        .sort(function (a, b) { return a.time.getTime() - b.time.getTime(); });
    return allEvents.map(function (event) {
        var time = format(event.time, 'HH:mm');
        return "".concat(time, " - ").concat(event.user, " ").concat(event.type);
    });
};
export var processMeetSummaryTableData = function (data) {
    return data.map(function (item) {
        return "User: ".concat(item.user.name || item.user.email, ", Duration: ").concat(item.duration, ", Presentation Count: ").concat(item.presentationCount);
    });
};
export var processDocumentAlertsChartData = function (data) {
    return data.map(function (item) { return "".concat(item.name, ": alerts count ").concat(item.count); });
};
function formatValueWithComparisons(value, comparisons) {
    var details = comparisons.filter(Boolean).join(', ');
    return details ? "".concat(value, " (").concat(details, ")") : String(value);
}
var optionalTeamAvgDiff = function (teamAverage) {
    return teamAverage ? "Team Avg: ".concat(teamAverage.diffString) : null;
};
var TIME_SPENT_BY_ACTIVITY_TYPE_KEYS = [
    'GMAIL',
    'DRIVE',
    'CHAT',
    'MEET',
    'CALENDAR',
    'GEMINI'
];
export var processTimeSpentOnWorkActivitiesUserChartData = function (data) {
    return data.map(function (row) {
        var comparisons = [
            "Previous: ".concat(formatDuration(row.previous, 'seconds')),
            "Current: ".concat(formatDuration(row.current, 'seconds')),
            row.teamAverage !== undefined
                ? "Team Avg: ".concat(formatDuration(row.teamAverage, 'seconds'))
                : null
        ];
        return "".concat(row.name, ": ").concat(comparisons.filter(Boolean).join(', '));
    });
};
export var processTimeSpentByActivityTypeData = function (data) {
    return data.current.map(function (period, index) {
        var _a;
        var previousPeriod = data.previous[index];
        var teamPeriod = (_a = data.teamAverage) === null || _a === void 0 ? void 0 : _a[index];
        var activityParts = TIME_SPENT_BY_ACTIVITY_TYPE_KEYS.map(function (key) {
            var currentValue = formatDuration(period[key], 'seconds');
            var previousValue = previousPeriod
                ? formatDuration(previousPeriod[key], 'seconds')
                : 'n/a';
            var teamValue = teamPeriod
                ? formatDuration(teamPeriod[key], 'seconds')
                : null;
            return teamValue
                ? "".concat(key, ": ").concat(currentValue, " (Prev: ").concat(previousValue, ", Team: ").concat(teamValue, ")")
                : "".concat(key, ": ").concat(currentValue, " (Prev: ").concat(previousValue, ")");
        });
        return "".concat(period.name, ": ").concat(activityParts.join(', '));
    });
};
export var processCreatedItemsByTypeData = function (data) {
    return data.map(function (item) {
        var teamAverageLine = item.teamAverage
            ? "Team Average: ".concat(item.teamAverage.value, " (").concat(item.teamAverage.diff.diffString, ")")
            : '';
        return "\n      Item Type: ".concat(item.label, "\n      Created: ").concat(item.created, "\n      Previous Period: ").concat(item.previousPeriod.value, " (").concat(item.previousPeriod.diff.diffString, ")\n      ").concat(teamAverageLine, "\n    ");
    });
};
export var processMostPopularDocumentsData = function (data) {
    return data.map(function (item) {
        return "\n      Document: ".concat(item.title, "\n      Current Period Views: ").concat(item.currentPeriod.views, "\n      Change from Previous Period (Views): ").concat(item.previousPeriod.views.diffString, "\n      Current Period Edits: ").concat(item.currentPeriod.edits, "\n      Change from Previous Period (Edits): ").concat(item.previousPeriod.edits.diffString, "\n      Current Period Comments: ").concat(item.currentPeriod.comments, "\n      Change from Previous Period (Comments): ").concat(item.previousPeriod.comments.diffString, "\n      Current Period Downloads: ").concat(item.currentPeriod.downloads, "\n      Change from Previous Period (Downloads): ").concat(item.previousPeriod.downloads.diffString, "\n      Total Activity: ").concat(item.total, "\n    ");
    });
};
export var processCreatedItemsByActivityLabelData = function (data) {
    return data.map(function (item) {
        var teamAvg = item.teamAverage;
        return "\n      Label: ".concat(item.labelName, "\n      Meetings: ").concat(formatValueWithComparisons(item.meetings, [
            "Previous: ".concat(item.previousPeriod.meetings.diffString),
            teamAvg && "Team Avg: ".concat(teamAvg.meetings.diffString)
        ]), "\n      Documents: ").concat(formatValueWithComparisons(item.documents, [
            "Previous: ".concat(item.previousPeriod.documents.diffString),
            teamAvg && "Team Avg: ".concat(teamAvg.documents.diffString)
        ]), "\n      Chat Rooms: ").concat(formatValueWithComparisons(item.chatRooms, [
            "Previous: ".concat(item.previousPeriod.chatRooms.diffString),
            teamAvg && "Team Avg: ".concat(teamAvg.chatRooms.diffString)
        ]), "\n      Calendar Events: ").concat(formatValueWithComparisons(item.calendarEvents, [
            "Previous: ".concat(item.previousPeriod.calendarEvents.diffString),
            teamAvg && "Team Avg: ".concat(teamAvg.calendarEvents.diffString)
        ]), "\n    ");
    });
};
export var processTopRecurringEventsAndMeetingsData = function (data) {
    return data.map(function (item) {
        return "\n      Label: ".concat(item.labelName, "\n      Total Amount: ").concat(formatValueWithComparisons(item.totalAmount.current, [
            "Previous: ".concat(item.totalAmount.previous.diffString),
            item.totalAmount.teamAverage &&
                "Team Avg: ".concat(item.totalAmount.teamAverage.diffString)
        ]), "\n      Total Duration: ").concat(formatValueWithComparisons(item.totalDuration.current, [
            "Previous: ".concat(item.totalDuration.previous.diffString),
            item.totalDuration.teamAverage &&
                "Team Avg: ".concat(item.totalDuration.teamAverage.diffString)
        ]), "\n      Number of Participants: ").concat(formatValueWithComparisons(item.numberOfParticipants.current, ["Previous: ".concat(item.numberOfParticipants.previous.diffString)]), "\n    ");
    });
};
export var processUserActivityOnResolvingData = function (data) {
    return data.documents.map(function (item) {
        var commentsDiff = item.previousPeriod
            ? " (".concat(item.previousPeriod.commentsResolved.diffString, ")")
            : '';
        var suggestionsDiff = item.previousPeriod
            ? " (".concat(item.previousPeriod.suggestionsResolved.diffString, ")")
            : '';
        return "\n      Document: ".concat(item.documentTitle, "\n      Comments Resolved: ").concat(item.commentsResolved).concat(commentsDiff, "\n      Suggestions Resolved: ").concat(item.suggestionsResolved).concat(suggestionsDiff, "\n      Total Resolved: ").concat(item.totalResolved, "\n    ");
    });
};
export var processDocReviewCountData = function (data) {
    var periodData = data.periods.map(function (period) {
        var teamAccepted = period.teamAccepted != null
            ? ", Team Accepted: ".concat(period.teamAccepted)
            : '';
        var teamRejected = period.teamRejected != null
            ? ", Team Rejected: ".concat(period.teamRejected)
            : '';
        return "\n      Period: ".concat(period.period, "\n      Accepted: ").concat(period.accepted, " (Previous: ").concat(period.prevAccepted, ")").concat(teamAccepted, "\n      Rejected: ").concat(period.rejected, " (Previous: ").concat(period.prevRejected, ")").concat(teamRejected, "\n    ");
    });
    var totalsData = "\n    Totals:\n    Accepted: ".concat(formatValueWithComparisons(data.totals.accepted.current, [
        "Previous: ".concat(data.totals.accepted.previous.diffString),
        data.totals.accepted.teamAverage &&
            "Team Avg: ".concat(data.totals.accepted.teamAverage.diffString)
    ]), "\n    Rejected: ").concat(formatValueWithComparisons(data.totals.rejected.current, [
        "Previous: ".concat(data.totals.rejected.previous.diffString),
        data.totals.rejected.teamAverage &&
            "Team Avg: ".concat(data.totals.rejected.teamAverage.diffString)
    ]), "\n  ");
    return __spreadArray(__spreadArray([], periodData, true), [totalsData], false);
};
export var processInitiatedCommunicationData = function (data) {
    return data.map(function (item) {
        var channelName = item.channel.replace(/_/g, ' ').toLowerCase();
        var teamDiff = getPercentageDiff(item.avg, item.total, {
            comparisonType: diffComparisonType.teamAverage
        });
        return "".concat(channelName, ": ").concat(item.total, " total (").concat(item.userPrevPeriodDiff.diffString, ", Team: ").concat(teamDiff.diffString, "), avg: ").concat(item.avg);
    });
};
export var processReceivedCommunicationData = function (data) {
    return data.map(function (item) {
        var channelName = item.channel.replace(/_/g, ' ').toLowerCase();
        var teamDiff = getPercentageDiff(item.avg, item.count, {
            comparisonType: diffComparisonType.teamAverage
        });
        return "".concat(channelName, ": ").concat(item.count, " received (").concat(item.userPrevPeriodDiff.diffString, ", Team: ").concat(teamDiff.diffString, "), avg: ").concat(item.avg);
    });
};
export var processCommunicationByChannelData = function (data) {
    var initiatedData = processInitiatedCommunicationData(data.Initiated);
    var receivedData = processReceivedCommunicationData(data.Received);
    return __spreadArray(__spreadArray(__spreadArray([
        'Initiated Communication:'
    ], initiatedData, true), [
        '',
        'Received Communication:'
    ], false), receivedData, true);
};
export var processCommunicationByLabelData = function (data) {
    return [
        "\n    Label: ".concat(data.labelName, "\n    Documents created: ").concat(data.documents.count, " (Previous: ").concat(data.documents.userDiff.diffString, ", Team Avg: ").concat(data.documents.orgUnitAvgDiff.diffString, ")\n    Document comments: ").concat(data.drive.count, " (Previous: ").concat(data.drive.userDiff.diffString, ", Team Avg: ").concat(data.drive.orgUnitAvgDiff.diffString, ")\n    Chat: ").concat(data.chat.count, " (Previous: ").concat(data.chat.userDiff.diffString, ", Team Avg: ").concat(data.chat.orgUnitAvgDiff.diffString, ")\n    Meetings: ").concat(data.meetings.count, " (Previous: ").concat(data.meetings.userDiff.diffString, ", Team Avg: ").concat(data.meetings.orgUnitAvgDiff.diffString, ")\n  ")
    ];
};
export var processWorkingTimeAllocationByLabelData = function (data) {
    var prevPeriodDiff = function (diff) {
        return diff.diffString;
    };
    var teamAvgDiff = function (diff) {
        return diff ? ", Team Avg: ".concat(diff.diffString) : '';
    };
    return [
        "\n    Label: ".concat(data.labelName, "\n    Meetings: ").concat(data.meetings.count, ", ").concat(data.meetings.duration, " (Previous: ").concat(prevPeriodDiff(data.meetings.prevPeriodDiff)).concat(teamAvgDiff(data.meetings.teamAvgDiff), ")\n    Documents: ").concat(data.documents.count, " (Previous: ").concat(prevPeriodDiff(data.documents.prevPeriodDiff)).concat(teamAvgDiff(data.documents.teamAvgDiff), ")\n    Chat Messages: ").concat(data.chatMessages.count, " (Previous: ").concat(prevPeriodDiff(data.chatMessages.prevPeriodDiff)).concat(teamAvgDiff(data.chatMessages.teamAvgDiff), ")\n    Events: ").concat(data.events.count, " (Previous: ").concat(prevPeriodDiff(data.events.prevPeriodDiff)).concat(teamAvgDiff(data.events.teamAvgDiff), ")\n  ")
    ];
};
export var processCommunicationByPeerTypeData = function (data) {
    return [
        "\n    Peer Type: ".concat(data.peerType, "\n    Drive: ").concat(data.drive.count, " (Previous: ").concat(data.drive.userDiff.diffString, ", Team Avg: ").concat(data.drive.orgUnitAvgDiff.diffString, ")\n    Chat: ").concat(data.chat.count, " (Previous: ").concat(data.chat.userDiff.diffString, ", Team Avg: ").concat(data.chat.orgUnitAvgDiff.diffString, ")\n    Meetings: ").concat(data.meetings.count, ", ").concat(data.meetings.duration, " (Previous: ").concat(data.meetings.userDiff.diffString, ", Team Avg: ").concat(data.meetings.orgUnitAvgDiff.diffString, ")\n    Gmail: ").concat(data.gmail.count, " (Previous: ").concat(data.gmail.userDiff.diffString, ", Team Avg: ").concat(data.gmail.orgUnitAvgDiff.diffString, ")\n  ")
    ];
};
export var processInvocationsByAppData = function (data) {
    return data.map(function (item) {
        var teamInvocationsDiff = item.invocations.teamAvgDiff
            ? ", Team Avg: ".concat(item.invocations.teamAvgDiff.diffString)
            : '';
        var teamTimeDiff = item.timeSpent.teamAvgDiff
            ? ", Team Avg: ".concat(item.timeSpent.teamAvgDiff.diffString)
            : '';
        return "\n    App: ".concat(item.appName, "\n    Invocations: ").concat(item.invocations.count, " (Previous: ").concat(item.invocations.prevPeriodDiff.diffString).concat(teamInvocationsDiff, ")\n    Time Spent: ").concat(item.timeSpent.formatted, " (Previous: ").concat(item.timeSpent.prevPeriodDiff.diffString).concat(teamTimeDiff, ")\n  ");
    });
};
export var processInvocationsByActionData = function (data) {
    var formatRecord = function (record) {
        return Object.entries(record)
            .map(function (_a) {
            var appName = _a[0], value = _a[1];
            return "".concat(appName, ": ").concat(value);
        })
            .join(', ');
    };
    return data.map(function (period) {
        var teamText = period.team
            ? "\n    Team: ".concat(formatRecord(period.team))
            : '';
        return "\n    Period: ".concat(period.period, "\n    Current: ").concat(formatRecord(period.currentPeriod), "\n    Previous: ").concat(formatRecord(period.previousPeriod)).concat(teamText, "\n  ");
    });
};
export var processInvocationsByUserData = function (data) {
    return data.map(function (item) {
        var teamInvocationsDiff = item.invocations.teamAvgDiff
            ? ", Team Avg: ".concat(item.invocations.teamAvgDiff.diffString)
            : '';
        var teamTimeDiff = item.timeSpent.teamAvgDiff
            ? ", Team Avg: ".concat(item.timeSpent.teamAvgDiff.diffString)
            : '';
        return "\n    User: ".concat(item.userName, " (").concat(item.userEmail, ")\n    Invocations: ").concat(item.invocations.count, " (Previous: ").concat(item.invocations.prevPeriodDiff.diffString).concat(teamInvocationsDiff, ")\n    Time Spent: ").concat(item.timeSpent.formatted, " (Previous: ").concat(item.timeSpent.prevPeriodDiff.diffString).concat(teamTimeDiff, ")\n  ");
    });
};
export var processMeetingsByTimePeriodData = function (data) {
    return [
        "\n    Period: ".concat(data.period, "\n    Meetings: ").concat(data.items
            .map(function (item) {
            var lines = item.meetings
                .map(function (m) {
                var _a;
                var duration = formatDuration(m.durationMinutes * 60, 'seconds');
                var title = (_a = m.title) === null || _a === void 0 ? void 0 : _a.trim();
                var parts = __spreadArray(__spreadArray([m.date], (title ? [title] : []), true), [duration], false);
                return parts.join(' — ');
            })
                .join('; ');
            return "".concat(item.startTime, " - ").concat(item.endTime, " (").concat(item.count, "): ").concat(lines);
        })
            .join(', '), "\n  ")
    ];
};
export var processMostFrequentCommunicationPartnersData = function (data) {
    return data.map(function (item) {
        return "\n    Partner: ".concat(item.name, " (").concat(item.orgUnitName, ")\n    Meetings: ").concat(item.meetings.count, " (").concat(item.meetings.duration, ")\n    Documents: ").concat(item.documents, "\n    Chats: ").concat(item.chats, "\n    Gmail: ").concat(item.gmails, "\n  ");
    });
};
export var processCommunicationByWeekdaysData = function (data) {
    return data.map(function (item) {
        var formatChannel = function (label, duration, eventCount) { return "".concat(label, ": ").concat(eventCount, " events (").concat(duration, "s)"); };
        var current = [
            formatChannel('Emails', item.currentPeriod.gmailDuration, item.currentPeriod.gmailEventCount),
            formatChannel('Chats', item.currentPeriod.chatDuration, item.currentPeriod.chatEventCount),
            formatChannel('Meetings', item.currentPeriod.meetingsDuration, item.currentPeriod.meetingsEventCount),
            formatChannel('Documents', item.currentPeriod.docsDuration, item.currentPeriod.docsEventCount)
        ].join(', ');
        var previous = [
            formatChannel('Emails', item.previousPeriod.gmailDuration, item.previousPeriod.gmailEventCount),
            formatChannel('Chats', item.previousPeriod.chatDuration, item.previousPeriod.chatEventCount),
            formatChannel('Meetings', item.previousPeriod.meetingsDuration, item.previousPeriod.meetingsEventCount),
            formatChannel('Documents', item.previousPeriod.docsDuration, item.previousPeriod.docsEventCount)
        ].join(', ');
        var team = [
            formatChannel('Emails', item.team.gmailDuration, item.team.gmailEventCount),
            formatChannel('Chats', item.team.chatDuration, item.team.chatEventCount),
            formatChannel('Meetings', item.team.meetingsDuration, item.team.meetingsEventCount),
            formatChannel('Documents', item.team.docsDuration, item.team.docsEventCount)
        ].join(', ');
        return "".concat(item.weekday, " Current (").concat(current, "), Previous (").concat(previous, "), Team Average (").concat(team, ")");
    });
};
export var processChatRoomsByMessageVolumeData = function (data) {
    return data.map(function (item) {
        var topPosters = item.topPosters
            .map(function (poster) { return "".concat(poster.name, " (").concat(poster.posts, ")"); })
            .join(', ');
        return "".concat(item.roomName, " - Active participants: ").concat(item.activeParticipants, ", External: ").concat(item.externalPercent, "%, Messages: ").concat(item.messages, " (Previous: ").concat(item.prevMessages.diffString, "), Reactions: ").concat(item.reactions, " (Previous: ").concat(item.prevReactions.diffString, "), Top posters: ").concat(topPosters);
    });
};
export var processCollaborativeTimeData = function (data) {
    var totals = Object.entries(data.totals).map(function (_a) {
        var peerType = _a[0], stats = _a[1];
        var meetings = formatValueWithComparisons(stats.meetings, [
            "Previous: ".concat(stats.prevMeetings),
            stats.diffMeetings && "Diff: ".concat(stats.diffMeetings.diffString),
            stats.teamAvgMeetings && "Team Avg: ".concat(stats.teamAvgMeetings.diffString)
        ]);
        var chats = formatValueWithComparisons(stats.chats, [
            "Previous: ".concat(stats.prevChats),
            stats.diffChats && "Diff: ".concat(stats.diffChats.diffString),
            stats.teamAvgChats && "Team Avg: ".concat(stats.teamAvgChats.diffString)
        ]);
        var gmails = formatValueWithComparisons(stats.gmails, [
            "Previous: ".concat(stats.prevGmails),
            stats.diffGmails && "Diff: ".concat(stats.diffGmails.diffString),
            stats.teamAvgGmails && "Team Avg: ".concat(stats.teamAvgGmails.diffString)
        ]);
        return "".concat(peerType, ": Meetings ").concat(meetings, ", Chats ").concat(chats, ", Gmail ").concat(gmails);
    });
    return __spreadArray(['Collaborative Time Data:'], totals, true);
};
export var processChatResponseSpeedAndFrequencyData = function (data) {
    return data.map(function (item) {
        var teamResponsesDiff = item.teamAvgResponses
            ? ", Team Avg: ".concat(item.teamAvgResponses.diffString)
            : '';
        var teamResponseTimeDiff = item.teamAvgResponseTime
            ? ", Team Avg: ".concat(item.teamAvgResponseTime.diffString)
            : '';
        return "".concat(item.userName, ": ").concat(item.responses, " responses (Previous: ").concat(item.prevResponses.diffString).concat(teamResponsesDiff, "), avg response time: ").concat(item.avgResponseTime, " (Previous: ").concat(item.prevAvgResponseTime.diffString).concat(teamResponseTimeDiff, ")");
    });
};
export var processTopChattersData = function (data) {
    return data.map(function (item) {
        var department = item.department ? " (".concat(item.department, ")") : '';
        return "".concat(item.userName).concat(department, ": Messages sent ").concat(item.messagesSent, " (Previous: ").concat(item.prevMessagesSent.diffString, "), avg response time ").concat(item.avgResponseTime, " (Previous: ").concat(item.prevAvgResponseTime.diffString, "), messages received ").concat(item.messagesReceived, " (Previous: ").concat(item.prevMessagesReceived.diffString, ")");
    });
};
export var processConsistencyOfAttendanceInMeetsData = function (data) {
    var _a;
    var result = [];
    result.push("Total Attended: ".concat(data.totals.attendedCount, " meetings (").concat(data.totals.attendedDuration, "s, Prev: ").concat(data.totals.attendedDurationPrevPeriodDiff.diffString).concat(data.totals.attendedDurationTeamAvgDiff
        ? ", Team: ".concat(data.totals.attendedDurationTeamAvgDiff.diffString)
        : '', ")"));
    result.push("Total Missed: ".concat(data.totals.missedCount, " meetings (").concat(data.totals.missedDuration, "s, Prev: ").concat(data.totals.missedDurationPrevPeriodDiff.diffString).concat(data.totals.missedDurationTeamAvgDiff
        ? ", Team: ".concat(data.totals.missedDurationTeamAvgDiff.diffString)
        : '', ")"));
    data.periods.forEach(function (period) {
        var attended = period.meetings.filter(function (m) { return m.attendance; });
        var missed = period.meetings.filter(function (m) { return !m.attendance; });
        result.push("".concat(period.period, ": Attended ").concat(attended.length, " meetings, Missed ").concat(missed.length, " meetings"));
    });
    (_a = data.teamPeriods) === null || _a === void 0 ? void 0 : _a.forEach(function (teamPeriod) {
        result.push("".concat(teamPeriod.period, " Team Averages: Attended ").concat(formatDuration(teamPeriod.teamAverageAttended, 'seconds'), ", Missed ").concat(formatDuration(teamPeriod.teamAverageSkipped, 'seconds')));
    });
    return result;
};
export var processPlannedVSAdHocMeetingsData = function (data) {
    var result = [];
    result.push("Total Planned Meetings: ".concat(data.totals.planned.current, " (").concat(data.totals.planned.currentDuration, ")"));
    result.push("Planned Meetings Previous Period: ".concat(data.totals.planned.previous.diffString));
    if (data.totals.planned.teamAverage) {
        result.push("Planned Meetings Team Average: ".concat(data.totals.planned.teamAverage.diffString));
    }
    result.push("Total Ad-hoc Meetings: ".concat(data.totals.adhoc.current, " (").concat(data.totals.adhoc.currentDuration, ")"));
    result.push("Ad-hoc Meetings Previous Period: ".concat(data.totals.adhoc.previous.diffString));
    if (data.totals.adhoc.teamAverage) {
        result.push("Ad-hoc Meetings Team Average: ".concat(data.totals.adhoc.teamAverage.diffString));
    }
    data.periods.forEach(function (period) {
        var teamPlanned = period.teamPlanned != null ? ", Team Planned: ".concat(period.teamPlanned) : '';
        var teamAdhoc = period.teamAdhoc != null ? ", Team Ad-hoc: ".concat(period.teamAdhoc) : '';
        result.push("".concat(period.period, ": Planned ").concat(period.planned, ", Ad-hoc ").concat(period.adhoc, " (Previous: Planned ").concat(period.prevPlanned, ", Ad-hoc ").concat(period.prevAdhoc, ")").concat(teamPlanned).concat(teamAdhoc));
    });
    return result;
};
export var processGmailActionsOverTimeData = function (data) {
    var result = [];
    result.push("Sent: ".concat(formatValueWithComparisons(data.totals.sent.current, [
        data.totals.sent.previous.diffString,
        optionalTeamAvgDiff(data.totals.sent.teamAverage)
    ])));
    result.push("Received: ".concat(formatValueWithComparisons(data.totals.received.current, [
        data.totals.received.previous.diffString,
        optionalTeamAvgDiff(data.totals.received.teamAverage)
    ])));
    result.push("Opened: ".concat(formatValueWithComparisons(data.totals.opened.current, [
        data.totals.opened.previous.diffString,
        optionalTeamAvgDiff(data.totals.opened.teamAverage)
    ])));
    result.push("Replied: ".concat(formatValueWithComparisons(data.totals.replied.current, [
        data.totals.replied.previous.diffString,
        optionalTeamAvgDiff(data.totals.replied.teamAverage)
    ])));
    result.push("Link Clicked: ".concat(formatValueWithComparisons(data.totals.linkClicked.current, [
        data.totals.linkClicked.previous.diffString,
        optionalTeamAvgDiff(data.totals.linkClicked.teamAverage)
    ])));
    result.push("Other Events: ".concat(formatValueWithComparisons(data.totals.otherEvents.current, [
        data.totals.otherEvents.previous.diffString,
        optionalTeamAvgDiff(data.totals.otherEvents.teamAverage)
    ])));
    data.periods.forEach(function (period) {
        result.push("".concat(period.period, ": Sent ").concat(period.sent, ", Received ").concat(period.received, ", Opened ").concat(period.opened, ", Replied ").concat(period.replied, ", Link Clicked ").concat(period.linkClicked, ", Other ").concat(period.otherEvents, " (Previous: Sent ").concat(period.prevSent, ", Received ").concat(period.prevReceived, ", Opened ").concat(period.prevOpened, ", Replied ").concat(period.prevReplied, ", Link Clicked ").concat(period.prevLinkClicked, ", Other ").concat(period.prevOtherEvents, "; Team: Sent ").concat(period.teamSent, ", Received ").concat(period.teamReceived, ", Opened ").concat(period.teamOpened, ", Replied ").concat(period.teamReplied, ", Link Clicked ").concat(period.teamLinkClicked, ", Other ").concat(period.teamOtherEvents, ")"));
    });
    return result;
};
export var processEmailSummaryReplyRateData = function (data) {
    var formatTeamDiff = function (teamAverage) {
        return teamAverage ? ", Team: ".concat(teamAverage.diffString) : '';
    };
    var result = [];
    result.push("Emails received: ".concat(data.totals.received.count, " (").concat(data.totals.received.previous.diffString).concat(formatTeamDiff(data.totals.received.teamAverage), ")"));
    result.push("Emails opened: ".concat(data.totals.opened.count, " / ").concat(data.totals.opened.rate, "% of received (").concat(data.totals.opened.previous.diffString).concat(formatTeamDiff(data.totals.opened.teamAverage), ")"));
    result.push("Emails replied: ".concat(data.totals.replied.count, " / ").concat(data.totals.replied.rate, "% of received (").concat(data.totals.replied.previous.diffString).concat(formatTeamDiff(data.totals.replied.teamAverage), ")"));
    data.periods.forEach(function (period) {
        result.push("".concat(period.period, ": Received ").concat(period.received, ", Opened ").concat(period.opened, ", Replied ").concat(period.replied, " (Previous: Received ").concat(period.prevReceived, ", Opened ").concat(period.prevOpened, ", Replied ").concat(period.prevReplied, ")"));
    });
    return result;
};
export var formatMeetingsBucketAnalyticRowLine = function (row) {
    var base = "".concat(row.key, ": Share ").concat(row.shareOfMeetings.current, "% (").concat(row.shareOfMeetings.previous.diffString, "), Count ").concat(row.meetingCount.current, " (").concat(row.meetingCount.previous.diffString, "), Duration ").concat(row.durationSeconds.current, "s (").concat(row.durationSeconds.previous.diffString, ")");
    var teamMetrics = [
        row.manHoursSeconds != null
            ? "Man-hours ".concat(row.manHoursSeconds.current, "s (").concat(row.manHoursSeconds.previous.diffString, ")")
            : null,
        row.avgParticipants != null
            ? "Avg participants ".concat(row.avgParticipants.current, " (").concat(row.avgParticipants.previous.diffString, ")")
            : null
    ].filter(Boolean);
    return teamMetrics.length > 0 ? "".concat(base, ", ").concat(teamMetrics.join(', ')) : base;
};
export var processMeetingLoadAnalyticData = function (data) {
    var result = [];
    var formatTeamDiff = function (teamAverage) {
        return teamAverage ? ", Team: ".concat(teamAverage.diffString) : '';
    };
    var includeTeamMetrics = data.totals.manHoursSeconds != null && data.totals.avgParticipants != null;
    result.push("Meeting count: ".concat(data.totals.meetingCount.current, " (").concat(data.totals.meetingCount.previous.diffString).concat(formatTeamDiff(data.totals.meetingCount.teamAverage), ")"));
    result.push("Duration (seconds): ".concat(data.totals.durationSeconds.current, " (").concat(data.totals.durationSeconds.previous.diffString).concat(formatTeamDiff(data.totals.durationSeconds.teamAverage), ")"));
    if (includeTeamMetrics) {
        result.push("Man-hours (seconds): ".concat(data.totals.manHoursSeconds.current, " (").concat(data.totals.manHoursSeconds.previous.diffString).concat(formatTeamDiff(data.totals.manHoursSeconds.teamAverage), ")"));
        result.push("Avg participants: ".concat(data.totals.avgParticipants.current, " (").concat(data.totals.avgParticipants.previous.diffString).concat(formatTeamDiff(data.totals.avgParticipants.teamAverage), ")"));
    }
    data.periods.forEach(function (period) {
        var _a, _b, _c, _d, _e, _f;
        var teamParts = [
            period.teamMeetingCount
                ? "Meetings ".concat(period.teamMeetingCount, ", Duration ").concat(period.teamDurationSeconds, "s")
                : null,
            includeTeamMetrics
                ? "Man-hours ".concat((_a = period.manHoursSeconds) !== null && _a !== void 0 ? _a : 0, "s, Avg participants ").concat((_b = period.avgParticipants) !== null && _b !== void 0 ? _b : 0)
                : null
        ].filter(Boolean);
        var teamSuffix = teamParts.length > 0 ? ", Team: ".concat(teamParts.join(', ')) : '';
        var prevParts = __spreadArray([
            "Meetings ".concat(period.prevMeetingCount),
            "Duration ".concat(period.prevDurationSeconds, "s")
        ], (includeTeamMetrics
            ? [
                "Man-hours ".concat((_c = period.prevManHoursSeconds) !== null && _c !== void 0 ? _c : 0, "s"),
                "Avg participants ".concat((_d = period.prevAvgParticipants) !== null && _d !== void 0 ? _d : 0)
            ]
            : []), true).join(', ');
        var currentParts = __spreadArray([
            "Meetings ".concat(period.meetingCount),
            "Duration ".concat(period.durationSeconds, "s")
        ], (includeTeamMetrics
            ? [
                "Man-hours ".concat((_e = period.manHoursSeconds) !== null && _e !== void 0 ? _e : 0, "s"),
                "Avg participants ".concat((_f = period.avgParticipants) !== null && _f !== void 0 ? _f : 0)
            ]
            : []), true).join(', ');
        result.push("".concat(period.period, ": ").concat(currentParts, " (Previous: ").concat(prevParts, ")").concat(teamSuffix));
    });
    return result;
};
export var processMeetingsBySizeAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetingsByLengthAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetingsByScheduleAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetingsBackToBackAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetingsByWhenAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetingsByWhoAttendedAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetingsPlannedVsActualAnalyticData = function (data) { return data.rows.map(formatMeetingsBucketAnalyticRowLine); };
export var processMeetSummaryData = function (data) {
    var formatTeamDiff = function (teamAverage) { return (teamAverage ? ", Team: ".concat(teamAverage.diffString) : ''); };
    return __spreadArray(__spreadArray([
        "Meetings Attended: ".concat(data.meetingsAttended.count, " (").concat(data.meetingsAttended.previous.diffString).concat(formatTeamDiff(data.meetingsAttended.teamAverage), ")"),
        "Total Duration: ".concat(data.totalDuration.seconds, "s (").concat(data.totalDuration.previous.diffString).concat(formatTeamDiff(data.totalDuration.teamAverage), ")")
    ], (data.manHours
        ? [
            "Man-hours: ".concat(data.manHours.seconds, "s (").concat(data.manHours.previous.diffString).concat(formatTeamDiff(data.manHours.teamAverage), ")")
        ]
        : []), true), [
        "1:1 vs Group: ".concat(data.oneToOneVsGroup.left.share, "% (").concat(data.oneToOneVsGroup.left.previous.diffString, ") / ").concat(data.oneToOneVsGroup.right.share, "% (").concat(data.oneToOneVsGroup.right.previous.diffString, ")"),
        "Internal vs External: ".concat(data.internalVsExternal.left.share, "% (").concat(data.internalVsExternal.left.previous.diffString, ") / ").concat(data.internalVsExternal.right.share, "% (").concat(data.internalVsExternal.right.previous.diffString, ")"),
        "Recurring vs Ad-hoc: ".concat(data.recurringVsAdhoc.left.share, "% (").concat(data.recurringVsAdhoc.left.previous.diffString, ") / ").concat(data.recurringVsAdhoc.right.share, "% (").concat(data.recurringVsAdhoc.right.previous.diffString, ")")
    ], false);
};
export var processGmailSummaryData = function (data) {
    var formatTeamDiff = function (teamAverage) { return (teamAverage ? ", Team: ".concat(teamAverage.diffString) : ''); };
    var formatAudience = function (label, metric) {
        return [
            "".concat(label, ": ").concat(metric.count, " (").concat(metric.previous.diffString).concat(formatTeamDiff(metric.teamAverage), ")"),
            "  In-team: ".concat(metric.breakdown.inTeam.count, " / ").concat(metric.breakdown.inTeam.share, "% (").concat(metric.breakdown.inTeam.previous.diffString, ")"),
            "  Cross-team: ".concat(metric.breakdown.crossTeam.count, " / ").concat(metric.breakdown.crossTeam.share, "% (").concat(metric.breakdown.crossTeam.previous.diffString, ")"),
            "  External: ".concat(metric.breakdown.external.count, " / ").concat(metric.breakdown.external.share, "% (").concat(metric.breakdown.external.previous.diffString, ")")
        ].join('\n');
    };
    return [
        formatAudience('Sent Emails', data.sent),
        formatAudience('Received Emails', data.received),
        "Internal Reply Rate: ".concat(data.internalReplyRate.rate, "% (").concat(data.internalReplyRate.previous.diffString).concat(formatTeamDiff(data.internalReplyRate.teamAverage), ") \u2014 ").concat(data.internalReplyRate.numerator, " of ").concat(data.internalReplyRate.denominator, " internal sent got a reply from colleagues"),
        "Internal Sent with No Reply (7+ days): ".concat(data.internalSentWithNoReply.count, " / ").concat(data.internalSentWithNoReply.share, "% (").concat(data.internalSentWithNoReply.previous.diffString).concat(formatTeamDiff(data.internalSentWithNoReply.teamAverage), ")"),
        "Replied Emails: ".concat(data.repliedEmails.count, " / ").concat(data.repliedEmails.share, "% (").concat(data.repliedEmails.previous.diffString).concat(formatTeamDiff(data.repliedEmails.teamAverage), ")"),
        "Opened Emails: ".concat(data.openedEmails.count, " / ").concat(data.openedEmails.share, "% (").concat(data.openedEmails.previous.diffString).concat(formatTeamDiff(data.openedEmails.teamAverage), ")"),
        "Sent vs Received Balance: ".concat(data.sentVsReceivedBalance.sentShare, "% (").concat(data.sentVsReceivedBalance.sentPrevious.diffString, ") vs ").concat(data.sentVsReceivedBalance.receivedShare, "% (").concat(data.sentVsReceivedBalance.receivedPrevious.diffString, ")"),
        "Forwarded Emails: ".concat(data.forwarded.count, " / ").concat(data.forwarded.share, "% (").concat(data.forwarded.previous.diffString).concat(formatTeamDiff(data.forwarded.teamAverage), ")")
    ];
};
export var processDriveSummaryData = function (data) {
    var formatMetric = function (label, metric) {
        return "".concat(label, ": ").concat(metric.count, " (").concat(metric.previous.diffString).concat(metric.teamAverage ? ", Team: ".concat(metric.teamAverage.diffString) : '', ")");
    };
    return [
        formatMetric('Created Documents', data.created),
        formatMetric('Edited', data.edited),
        formatMetric('Viewed', data.viewed),
        formatMetric('Comments Added', data.commentsAdded),
        formatMetric('Comments Resolved', data.commentsResolved),
        formatMetric('Suggestions Added', data.suggestionsAdded),
        formatMetric('Suggestions Resolved (accepted or rejected)', data.suggestionsResolved),
        formatMetric('Shared', data.shared),
        formatMetric('Shared Externally', data.sharedExternally)
    ];
};
export var processGeminiSummaryData = function (data) { return __spreadArray([
    "Users in scope: ".concat(data.usersInScope),
    "Total Gemini actions: ".concat(data.totalActions.count, " (").concat(data.totalActions.previous.diffString, ")"),
    "Adoption Rate: ".concat(data.adoptionRate.count, " of ").concat(data.usersInScope, " users, ").concat(data.adoptionRate.share, "% (").concat(data.adoptionRate.previous.diffString, ")"),
    "Users with no Gemini usage: ".concat(Math.max(data.usersInScope - data.adoptionRate.count, 0)),
    "Avg Actions per Active User: ".concat(data.avgActionsPerUser.count, " (").concat(data.avgActionsPerUser.previous.diffString, ")"),
    "Standalone Usage: ".concat(data.standaloneInAppUsage.left.share, "%, ").concat(data.standaloneInAppUsage.left.count, " actions (").concat(data.standaloneInAppUsage.left.previous.diffString, ")"),
    "In-App Usage: ".concat(data.standaloneInAppUsage.right.share, "%, ").concat(data.standaloneInAppUsage.right.count, " actions (").concat(data.standaloneInAppUsage.right.previous.diffString, ")")
], (data.usageLevel.period.isValid && data.usageLevel.level
    ? __spreadArray([
        "Most common usage level: ".concat(data.usageLevel.level)
    ], GEMINI_USAGE_LEVELS.map(function (level) {
        var levelData = data.usageLevel.distribution[level];
        return "".concat(level, ": ").concat(levelData.count, " users, ").concat(levelData.share, "% (").concat(levelData.previous.diffString, ")");
    }), true) : ['Usage level: unavailable for the selected period length']), true); };
// Prompts are always English, so the product names live here rather than going through i18n.
var GEMINI_USAGE_CATEGORY_LABELS = (_a = {},
    _a[GeminiUsageCategoryEnum.CHAT_AND_RESEARCH] = 'Chat & Research',
    _a[GeminiUsageCategoryEnum.CONTENT_CREATION] = 'Content creation',
    _a[GeminiUsageCategoryEnum.SUMMARIZE] = 'Summarize',
    _a[GeminiUsageCategoryEnum.WORKFLOW_ACTIONS] = 'Workflow Actions',
    _a);
var formatUserSummaryDiffs = function (metric) {
    return [
        "vs previous period: ".concat(metric.prevPeriodDiff.diffString),
        metric.teamAvgDiff
            ? "vs team average: ".concat(metric.teamAvgDiff.diffString)
            : null
    ]
        .filter(Boolean)
        .join(', ');
};
export var processGeminiUserSummaryData = function (data) {
    var _a, _b;
    return __spreadArray(__spreadArray(__spreadArray(__spreadArray([], (data.usageLevel.period.isValid && data.usageLevel.level
        ? [
            "Usage level: ".concat(data.usageLevel.level, " (").concat((_b = (_a = data.usageLevel.previous) === null || _a === void 0 ? void 0 : _a.diffString) !== null && _b !== void 0 ? _b : '0%', " vs previous period)")
        ]
        : ['Usage level: unavailable for the selected period length']), true), [
        "Total Gemini invocations: ".concat(data.totalActions.count, " (").concat(formatUserSummaryDiffs(data.totalActions), ")"),
        "Standalone Usage: ".concat(data.standaloneInAppUsage.left.share, "%, ").concat(data.standaloneInAppUsage.left.count, " actions (").concat(formatUserSummaryDiffs(data.standaloneInAppUsage.left), ")"),
        "In-App Usage: ".concat(data.standaloneInAppUsage.right.share, "%, ").concat(data.standaloneInAppUsage.right.count, " actions (").concat(formatUserSummaryDiffs(data.standaloneInAppUsage.right), ")")
    ], false), GEMINI_USAGE_CATEGORIES.map(function (category) {
        var categoryData = data.categories[category];
        return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(categoryData.count, " actions, ").concat(categoryData.share, "% of total (").concat(formatUserSummaryDiffs(categoryData), ")");
    }), true), [
        data.topApp.appName
            ? "Top in-app Gemini app: ".concat(data.topApp.appName, ", ").concat(data.topApp.count, " actions, ").concat(data.topApp.share, "% of total (").concat(formatUserSummaryDiffs(data.topApp), ")")
            : "Top in-app Gemini app: none, ".concat(data.topApp.count, " actions, ").concat(data.topApp.share, "% of total (").concat(formatUserSummaryDiffs(data.topApp), ")")
    ], false);
};
export var processGeminiAdoptionTrendData = function (data) {
    var result = ["Users in scope: ".concat(data.usersInScope)];
    data.weeks.forEach(function (week) {
        if (week.userLevel !== null) {
            var teamLevel = week.teamUserLevel !== null ? ", Team level ".concat(week.teamUserLevel) : '';
            result.push("".concat(week.period, " (").concat(week.dateFrom, " - ").concat(week.dateTo, "): Usage level ").concat(week.userLevel).concat(teamLevel));
            return;
        }
        var levelBreakdown = GEMINI_USAGE_LEVELS.map(function (level) { return "".concat(level, ": ").concat(week.levels[level], " users"); }).join(', ');
        result.push("".concat(week.period, " (").concat(week.dateFrom, " - ").concat(week.dateTo, "): ").concat(levelBreakdown));
    });
    return result;
};
export var processGeminiUsageLevelDistributionData = function (data) {
    var result = ["Users in scope: ".concat(data.usersInScope)];
    GEMINI_USAGE_LEVELS.forEach(function (level) {
        var summaryMetric = data.summary[level];
        var teamAverage = summaryMetric.teamAverage;
        result.push("Summary ".concat(level, ": ").concat(summaryMetric.count, " users (").concat(summaryMetric.share, "%, ").concat(summaryMetric.previous.diffString).concat(teamAverage ? ", Team Avg: ".concat(teamAverage.diffString) : '', ")"));
    });
    data.segments.forEach(function (segment) {
        var _a;
        if (segment.userCurrentLevel !== null &&
            segment.userCurrentLevel !== undefined) {
            var teamLevel = segment.teamLevels != null
                ? ", team ".concat(Object.entries(segment.teamLevels)
                    .map(function (_a) {
                    var level = _a[0], count = _a[1];
                    return "".concat(level, ": ").concat(count);
                })
                    .join(', '))
                : '';
            result.push("".concat(segment.name, ": current ").concat(segment.userCurrentLevel, ", previous ").concat((_a = segment.userPreviousLevel) !== null && _a !== void 0 ? _a : 'n/a').concat(teamLevel));
            return;
        }
        var currentBreakdown = GEMINI_USAGE_LEVELS.map(function (level) { return "".concat(level, ": ").concat(segment.currentLevels[level]); }).join(', ');
        var previousBreakdown = GEMINI_USAGE_LEVELS.map(function (level) { return "".concat(level, ": ").concat(segment.previousLevels[level]); }).join(', ');
        result.push("".concat(segment.name, ": current (").concat(currentBreakdown, "); previous (").concat(previousBreakdown, ")"));
    });
    return result;
};
export var processGeminiUsageCategoryByAppData = function (data) {
    var result = [];
    GEMINI_USAGE_CATEGORIES.forEach(function (category) {
        var summaryMetric = data.summary[category];
        var teamAverage = summaryMetric.teamAverage;
        result.push("Summary ".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(summaryMetric.count, " actions (").concat(summaryMetric.share, "%, ").concat(summaryMetric.previous.diffString).concat(teamAverage ? ", Team Avg: ".concat(teamAverage.diffString) : '', ")"));
    });
    data.apps.forEach(function (segment) {
        var currentBreakdown = GEMINI_USAGE_CATEGORIES.map(function (category) {
            return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(segment.current[category]);
        }).join(', ');
        var previousBreakdown = GEMINI_USAGE_CATEGORIES.map(function (category) {
            return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(segment.previous[category]);
        }).join(', ');
        var teamCounts = segment.team;
        var teamBreakdown = teamCounts
            ? GEMINI_USAGE_CATEGORIES.map(function (category) {
                return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(teamCounts[category]);
            }).join(', ')
            : null;
        result.push("".concat(segment.appName, ": current (").concat(currentBreakdown, "); previous (").concat(previousBreakdown, ")").concat(teamBreakdown ? "; team (".concat(teamBreakdown, ")") : ''));
    });
    return result;
};
export var processGeminiActionBreakdownData = function (data) {
    var result = ["View: ".concat(data.view)];
    GEMINI_USAGE_CATEGORIES.forEach(function (category) {
        var summaryMetric = data.summary[category];
        var teamAverage = summaryMetric.teamAverage;
        result.push("Summary ".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(summaryMetric.count, " actions (").concat(summaryMetric.share, "%, ").concat(summaryMetric.previous.diffString).concat(teamAverage ? ", Team Avg: ".concat(teamAverage.diffString) : '', ")"));
    });
    data.segments.forEach(function (segment) {
        var currentBreakdown = GEMINI_USAGE_CATEGORIES.map(function (category) {
            return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(segment.current[category]);
        }).join(', ');
        var previousBreakdown = GEMINI_USAGE_CATEGORIES.map(function (category) {
            return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(segment.previous[category]);
        }).join(', ');
        var teamCounts = segment.team;
        var teamBreakdown = teamCounts
            ? GEMINI_USAGE_CATEGORIES.map(function (category) {
                return "".concat(GEMINI_USAGE_CATEGORY_LABELS[category], ": ").concat(teamCounts[category]);
            }).join(', ')
            : null;
        result.push("".concat(segment.name, ": current (").concat(currentBreakdown, "); previous (").concat(previousBreakdown, ")").concat(teamBreakdown ? "; team (".concat(teamBreakdown, ")") : ''));
    });
    return result;
};
export var processChatSummaryData = function (data) {
    var formatTeamDiff = function (teamAverage) { return (teamAverage ? ", Team: ".concat(teamAverage.diffString) : ''); };
    var formatCountMetric = function (metric) {
        return "".concat(metric.count, " (").concat(metric.previous.diffString).concat(formatTeamDiff(metric.teamAverage), ")");
    };
    return [
        "Messages Sent / Received: ".concat(formatCountMetric(data.messagesSentReceived.left), " / ").concat(formatCountMetric(data.messagesSentReceived.right)),
        "Reactions Sent / Received: ".concat(formatCountMetric(data.reactionsSentReceived.left), " / ").concat(formatCountMetric(data.reactionsSentReceived.right)),
        "Where Chat Happens \u2014 In-team: ".concat(data.whereChatHappens.inTeam.share, "% (").concat(data.whereChatHappens.inTeam.previous.diffString, ")"),
        "Where Chat Happens \u2014 Cross-team: ".concat(data.whereChatHappens.crossTeam.share, "% (").concat(data.whereChatHappens.crossTeam.previous.diffString, ")"),
        "Where Chat Happens \u2014 External: ".concat(data.whereChatHappens.external.share, "% (").concat(data.whereChatHappens.external.previous.diffString, ")"),
        "1:1 vs Group: ".concat(data.oneToOneVsGroup.oneToOneShare, "% / ").concat(data.oneToOneVsGroup.groupShare, "% (").concat(data.oneToOneVsGroup.sharePrevious.diffString, ")"),
        "1:1 rooms / groups: ".concat(formatCountMetric(data.oneToOneRoomsGroups.left), " / ").concat(formatCountMetric(data.oneToOneRoomsGroups.right)),
        "Active Chatters / Avg Msg per day: ".concat(data.activeChattersAvgMsgPerSender.share
            ? "".concat(data.activeChattersAvgMsgPerSender.share.share, "% (").concat(data.activeChattersAvgMsgPerSender.share.previous.diffString, ") / ")
            : '').concat(formatCountMetric(data.activeChattersAvgMsgPerSender.count)),
        "External share / Messages: ".concat(data.externalShareMessages.left.share, "% (").concat(data.externalShareMessages.left.previous.diffString, ") / ").concat(data.externalShareMessages.right.share, "% (").concat(data.externalShareMessages.right.previous.diffString, ")"),
        "External Partners (1:1 chats only): ".concat(data.externalPartners.count).concat(data.externalPartners.previous.diff > 0 ||
            data.externalPartners.teamAverage
            ? " (".concat([
                data.externalPartners.previous.diff > 0
                    ? data.externalPartners.previous.diffString
                    : null,
                data.externalPartners.teamAverage
                    ? "Team: ".concat(data.externalPartners.teamAverage.diffString)
                    : null
            ]
                .filter(Boolean)
                .join(', '), ")")
            : '')
    ];
};
export var processChatActivityBreakdownData = function (data) {
    var formatTeamDiff = function (teamAverage) {
        return teamAverage ? ", Team: ".concat(teamAverage.diffString) : '';
    };
    var result = [
        "Sent messages: ".concat(data.totals.messagesSent.current, " (").concat(data.totals.messagesSent.previous.diffString).concat(formatTeamDiff(data.totals.messagesSent.teamAverage), ")"),
        "Sent reactions: ".concat(data.totals.reactionsSent.current, " (").concat(data.totals.reactionsSent.previous.diffString).concat(formatTeamDiff(data.totals.reactionsSent.teamAverage), ")"),
        "Received messages: ".concat(data.totals.messagesReceived.current, " (").concat(data.totals.messagesReceived.previous.diffString).concat(formatTeamDiff(data.totals.messagesReceived.teamAverage), ")"),
        "Received reactions: ".concat(data.totals.reactionsReceived.current, " (").concat(data.totals.reactionsReceived.previous.diffString).concat(formatTeamDiff(data.totals.reactionsReceived.teamAverage), ")")
    ];
    data.periods.forEach(function (period) {
        result.push("".concat(period.period, ": Sent messages ").concat(period.messagesSent, ", Sent reactions ").concat(period.reactionsSent, ", Received messages ").concat(period.messagesReceived, ", Received reactions ").concat(period.reactionsReceived));
    });
    return result;
};
export var processDriveDocumentsActivityData = function (data) {
    var typeLabels = {
        document: 'Docs',
        spreadsheet: 'Sheets',
        presentation: 'Presentations',
        other: 'Other'
    };
    var result = [];
    for (var _i = 0, _a = Object.entries(data); _i < _a.length; _i++) {
        var _b = _a[_i], typeKey = _b[0], metrics = _b[1];
        var label = typeLabels[typeKey];
        var formatMetricLine = function (metricLabel, metric) {
            return "".concat(metricLabel, ": ").concat(metric.count, " (Previous: ").concat(metric.previous.diffString).concat(optionalTeamAvgDiff(metric.teamAverage), ")");
        };
        result.push(formatMetricLine("".concat(label, " Created"), metrics.created));
        result.push(formatMetricLine("".concat(label, " Edited"), metrics.edited));
        result.push(formatMetricLine("".concat(label, " Viewed"), metrics.viewed));
        result.push("".concat(label, " Shared / Externally: ").concat(metrics.shared.left.count, " (").concat(metrics.shared.left.previous.diffString).concat(optionalTeamAvgDiff(metrics.shared.left.teamAverage), ") / ").concat(metrics.shared.right.count, " (").concat(metrics.shared.right.previous.diffString).concat(optionalTeamAvgDiff(metrics.shared.right.teamAverage), ")"));
        result.push("".concat(label, " Comments Added / Resolved: ").concat(metrics.comments.left.count, " (").concat(metrics.comments.left.previous.diffString).concat(optionalTeamAvgDiff(metrics.comments.left.teamAverage), ") / ").concat(metrics.comments.right.count, " (").concat(metrics.comments.right.previous.diffString).concat(optionalTeamAvgDiff(metrics.comments.right.teamAverage), ")"));
        result.push("".concat(label, " Suggestions Added / Resolved: ").concat(metrics.suggestions.left.count, " (").concat(metrics.suggestions.left.previous.diffString).concat(optionalTeamAvgDiff(metrics.suggestions.left.teamAverage), ") / ").concat(metrics.suggestions.right.count, " (").concat(metrics.suggestions.right.previous.diffString).concat(optionalTeamAvgDiff(metrics.suggestions.right.teamAverage), ")"));
    }
    return result;
};
export var processDriveActionsOverTimeData = function (data) {
    var result = [];
    result.push("Create: ".concat(formatValueWithComparisons(data.totals.created.current, [
        data.totals.created.previous.diffString,
        optionalTeamAvgDiff(data.totals.created.teamAverage)
    ])));
    result.push("Edit: ".concat(formatValueWithComparisons(data.totals.edited.current, [
        data.totals.edited.previous.diffString,
        optionalTeamAvgDiff(data.totals.edited.teamAverage)
    ])));
    result.push("View: ".concat(formatValueWithComparisons(data.totals.viewed.current, [
        data.totals.viewed.previous.diffString,
        optionalTeamAvgDiff(data.totals.viewed.teamAverage)
    ])));
    result.push("Post comment: ".concat(formatValueWithComparisons(data.totals.commentsPosted.current, [
        data.totals.commentsPosted.previous.diffString,
        optionalTeamAvgDiff(data.totals.commentsPosted.teamAverage)
    ])));
    result.push("Post suggestion: ".concat(formatValueWithComparisons(data.totals.suggestionsPosted.current, [
        data.totals.suggestionsPosted.previous.diffString,
        optionalTeamAvgDiff(data.totals.suggestionsPosted.teamAverage)
    ])));
    result.push("Other events: ".concat(formatValueWithComparisons(data.totals.otherEvents.current, [
        data.totals.otherEvents.previous.diffString,
        optionalTeamAvgDiff(data.totals.otherEvents.teamAverage)
    ])));
    data.periods.forEach(function (period) {
        result.push("".concat(period.period, ": Create ").concat(period.created, ", Edit ").concat(period.edited, ", View ").concat(period.viewed, ", Post comment ").concat(period.commentsPosted, ", Post suggestion ").concat(period.suggestionsPosted, ", Other ").concat(period.otherEvents, " (Previous: Create ").concat(period.prevCreated, ", Edit ").concat(period.prevEdited, ", View ").concat(period.prevViewed, ", Post comment ").concat(period.prevCommentsPosted, ", Post suggestion ").concat(period.prevSuggestionsPosted, ", Other ").concat(period.prevOtherEvents, "; Team: Create ").concat(period.teamCreated, ", Edit ").concat(period.teamEdited, ", View ").concat(period.teamViewed, ", Post comment ").concat(period.teamCommentsPosted, ", Post suggestion ").concat(period.teamSuggestionsPosted, ", Other ").concat(period.teamOtherEvents, ")"));
    });
    return result;
};
export var processWorkdayAlignmentData = function (data) {
    var result = [];
    data.forEach(function (item) {
        var userWorkday = item.startWorkTime && item.endWorkTime
            ? "".concat(item.startWorkTime, " - ").concat(item.endWorkTime)
            : 'No data';
        var teamAverage = item.teamAverageStartWorkTime && item.teamAverageEndWorkTime
            ? "".concat(item.teamAverageStartWorkTime, " - ").concat(item.teamAverageEndWorkTime)
            : 'No data';
        result.push("".concat(item.period, ": User workday: ").concat(userWorkday, ", Team average: ").concat(teamAverage));
    });
    return result;
};
export var processCreatedAndOwnedFilesData = function (data) {
    var result = [];
    result.push("Total Created and Owned Files: ".concat(data.totals.createdAndOwnedFiles));
    result.push("Total Previous Period: ".concat(data.totals.previousPeriod));
    result.push("Total Team Average: ".concat(data.totals.teamAverage));
    data.periods.forEach(function (item) {
        result.push("".concat(item.period, ": Created and owned files: ").concat(item.createdAndOwnedFiles, ", Previous period: ").concat(item.previousPeriod, ", Team average: ").concat(item.teamAverage));
    });
    return result;
};
export var processSharedFilesData = function (data) {
    var _a, _b, _c, _d;
    var result = [];
    result.push("Total User Internal Sharing: ".concat(data.totals.userInternalSharing));
    result.push("Total User External Sharing: ".concat(data.totals.userExternalSharing));
    result.push("Total Previous Internal Sharing: ".concat(data.totals.previousInternalSharing));
    result.push("Total Previous External Sharing: ".concat(data.totals.previousExternalSharing));
    result.push("Team Diff Internal: ".concat((_b = (_a = data.totals.teamDiffInternal) === null || _a === void 0 ? void 0 : _a.diffString) !== null && _b !== void 0 ? _b : '0%'));
    result.push("Team Diff External: ".concat((_d = (_c = data.totals.teamDiffExternal) === null || _c === void 0 ? void 0 : _c.diffString) !== null && _d !== void 0 ? _d : '0%'));
    data.periodsData.forEach(function (item) {
        result.push("".concat(item.period, ": Previous Internal ").concat(item.previousInternalSharing, ", External ").concat(item.previousExternalSharing, ", User Internal ").concat(item.userInternalSharing, ", External ").concat(item.userExternalSharing, ", Team Internal ").concat(item.teamInternalSharing, ", External ").concat(item.teamExternalSharing));
    });
    return result;
};
export var processTimeSpentOnActivitiesData = function (data) {
    var result = [];
    var _a = data.totals, teamAverageDiffs = _a.teamAverageDiffs, prevPeriodDiffs = _a.prevPeriodDiffs;
    var teamDiffs = teamAverageDiffs;
    result.push("Total Docs Comments Posted: ".concat(formatDuration(data.totals.docsCommentsPosted, 'seconds'), " (Prev: ").concat(prevPeriodDiffs.docsCommentsPosted.diffString, ", Team: ").concat(teamDiffs.docsCommentsPosted.diffString, ")"));
    result.push("Total Meetings Organized: ".concat(formatDuration(data.totals.meetingsOrganized, 'seconds'), " (Prev: ").concat(prevPeriodDiffs.meetingsOrganized.diffString, ", Team: ").concat(teamDiffs.meetingsOrganized.diffString, ")"));
    result.push("Total Chat Messages Sent: ".concat(formatDuration(data.totals.chatMessagesSent, 'seconds'), " (Prev: ").concat(prevPeriodDiffs.chatMessagesSent.diffString, ", Team: ").concat(teamDiffs.chatMessagesSent.diffString, ")"));
    result.push("Total Emails Sent: ".concat(formatDuration(data.totals.emailsSent, 'seconds'), " (Prev: ").concat(prevPeriodDiffs.emailsSent.diffString, ", Team: ").concat(teamDiffs.emailsSent.diffString, ")"));
    data.currentPeriod.forEach(function (item, idx) {
        var _a, _b, _c;
        var prev = (_a = data.previousPeriod[idx]) !== null && _a !== void 0 ? _a : emptyTimeSpentOnActivitiesPeriod;
        var team = (_c = (_b = data.teamAverage) === null || _b === void 0 ? void 0 : _b[idx]) !== null && _c !== void 0 ? _c : emptyTimeSpentOnActivitiesPeriod;
        result.push("".concat(item.period, ": Docs ").concat(formatDuration(item.docsCommentsPosted, 'seconds'), " (Prev: ").concat(formatDuration(prev.docsCommentsPosted, 'seconds'), ", Team: ").concat(formatDuration(team.docsCommentsPosted, 'seconds'), "), Meetings ").concat(formatDuration(item.meetingsOrganized, 'seconds'), " (Prev: ").concat(formatDuration(prev.meetingsOrganized, 'seconds'), ", Team: ").concat(formatDuration(team.meetingsOrganized, 'seconds'), "), Chat ").concat(formatDuration(item.chatMessagesSent, 'seconds'), " (Prev: ").concat(formatDuration(prev.chatMessagesSent, 'seconds'), ", Team: ").concat(formatDuration(team.chatMessagesSent, 'seconds'), "), Email ").concat(formatDuration(item.emailsSent, 'seconds'), " (Prev: ").concat(formatDuration(prev.emailsSent, 'seconds'), ", Team: ").concat(formatDuration(team.emailsSent, 'seconds'), ")"));
    });
    return result;
};
export var processWorkLocationBreakdownData = function (data) {
    var result = [];
    result.push("Total Office Hours: ".concat(formatDuration(data.totals.officeHours, 'hours'), " (Prev: ").concat(data.totals.officeHoursPrevPeriodDiff.diffString).concat(data.totals.officeHoursTeamAvgDiff
        ? ", Team Diff: ".concat(data.totals.officeHoursTeamAvgDiff.diffString)
        : '', ")"));
    result.push("Total Remote Hours: ".concat(formatDuration(data.totals.remoteHours, 'hours'), " (Prev: ").concat(data.totals.remoteHoursPrevPeriodDiff.diffString).concat(data.totals.remoteHoursTeamAvgDiff
        ? ", Team Diff: ".concat(data.totals.remoteHoursTeamAvgDiff.diffString)
        : '', ")"));
    data.periods.forEach(function (period) {
        var officeHours = formatDuration(period.officeHours, 'hours');
        var remoteHours = formatDuration(period.remoteHours, 'hours');
        var prevOfficeHours = formatDuration(period.previousOfficeHours, 'hours');
        var prevRemoteHours = formatDuration(period.previousRemoteHours, 'hours');
        result.push("".concat(period.period, ": Office ").concat(officeHours, " (Prev: ").concat(prevOfficeHours, ", ").concat(period.officeHoursPrevPeriodDiff.diffString).concat(period.officeHoursTeamAvgDiff && period.teamAverageOfficeHours !== null
            ? ", Team: ".concat(formatDuration(period.teamAverageOfficeHours, 'hours'), ", Team Diff: ").concat(period.officeHoursTeamAvgDiff.diffString)
            : '', "), Remote ").concat(remoteHours, " (Prev: ").concat(prevRemoteHours, ", ").concat(period.remoteHoursPrevPeriodDiff.diffString).concat(period.remoteHoursTeamAvgDiff && period.teamAverageRemoteHours !== null
            ? ", Team: ".concat(formatDuration(period.teamAverageRemoteHours, 'hours'), ", Team Diff: ").concat(period.remoteHoursTeamAvgDiff.diffString)
            : '', ")"));
    });
    return result;
};
export var processIndividualCommTimeData = function (data) {
    var result = [];
    result.push("Meet total: ".concat(formatDuration(data.totals.meet.current, 'seconds'), " (Prev: ").concat(data.totals.meet.previous.diffString, ")"));
    result.push("Chat total: ".concat(formatDuration(data.totals.chat.current, 'seconds'), " (Prev: ").concat(data.totals.chat.previous.diffString, ")"));
    data.teamMembers.forEach(function (member) {
        result.push("".concat(member.userName, ": Meet ").concat(member.meet, " (Prev: ").concat(member.prevMeet, "), Chat ").concat(member.chat, " (Prev: ").concat(member.prevChat, ")"));
    });
    return result;
};
export var processBreaksDuringWorkIndicatorData = function (data) {
    return data.map(function (item) {
        return "Date: ".concat(item.date, ", Breaks: ").concat(item.count);
    });
};
export var processDaysWithExtendedWorkHoursData = function (data) {
    return data.map(function (item) {
        return "Date: ".concat(format(item.date, 'yyyy-MM-dd'), ", Hours: ").concat(item.count);
    });
};
export var processDocsSharedExternallyData = function (data) {
    var result = [];
    var periodDiff = getPercentageDiff(data.totals.previousPeriod, data.totals.docsSharedExternally);
    var teamDiff = getPercentageDiff(data.totals.teamAverage, data.totals.docsSharedExternally, { comparisonType: diffComparisonType.teamAverage });
    result.push("Total Docs Shared Externally: ".concat(data.totals.docsSharedExternally, " (Previous: ").concat(periodDiff.diffString, ", Team: ").concat(teamDiff.diffString, ")"));
    result.push("Total Previous Period: ".concat(data.totals.previousPeriod));
    result.push("Total Team Average: ".concat(data.totals.teamAverage));
    data.periods.forEach(function (item) {
        result.push("".concat(item.period, ": Docs Shared Externally ").concat(item.docsSharedExternally, ", Previous Period ").concat(item.previousPeriod, ", Team Average ").concat(item.teamAverage));
    });
    return result;
};
export var processDocsSharedExternallyByLabelData = function (data) {
    return data.map(function (item) {
        var currentPeriod = "Documents: ".concat(item.documents, ", Spreadsheets: ").concat(item.spreadsheets, ", Presentations: ").concat(item.presentations, ", PDFs: ").concat(item.pdfs, ", Other: ").concat(item.other);
        var previousPeriod = "Previous Period - Documents: ".concat(item.previousPeriod.documents.diffString, ", Spreadsheets: ").concat(item.previousPeriod.spreadsheets.diffString, ", Presentations: ").concat(item.previousPeriod.presentations.diffString, ", PDFs: ").concat(item.previousPeriod.pdfs.diffString, ", Other: ").concat(item.previousPeriod.other.diffString);
        var teamAverage = item.teamAverage
            ? "Team Average - Documents: ".concat(item.teamAverage.documents.diffString, ", Spreadsheets: ").concat(item.teamAverage.spreadsheets.diffString, ", Presentations: ").concat(item.teamAverage.presentations.diffString, ", PDFs: ").concat(item.teamAverage.pdfs.diffString, ", Other: ").concat(item.teamAverage.other.diffString)
            : '';
        return "Label: ".concat(item.labelName, ", ").concat(currentPeriod, ", ").concat(previousPeriod).concat(teamAverage ? ", ".concat(teamAverage) : '');
    });
};
export var processScheduledMeetAttendanceRateData = function (data) {
    var _a;
    var result = [];
    result.push("Total Attended: ".concat(data.totals.attended.current, " meetings (").concat(data.totals.attended.currentDuration, ", Prev: ").concat(data.totals.attended.previous.diffString).concat(data.totals.attended.teamAverage
        ? ", Team: ".concat(data.totals.attended.teamAverage.diffString)
        : '', ")"));
    result.push("Total Skipped: ".concat(data.totals.skipped.current, " meetings (").concat(data.totals.skipped.currentDuration, ", Prev: ").concat(data.totals.skipped.previous.diffString).concat(data.totals.skipped.teamAverage
        ? ", Team: ".concat(data.totals.skipped.teamAverage.diffString)
        : '', ")"));
    data.periods.forEach(function (period) {
        result.push("".concat(period.period, ": Attended ").concat(period.attended, " meetings, Skipped ").concat(period.skipped, " meetings"));
    });
    (_a = data.teamPeriods) === null || _a === void 0 ? void 0 : _a.forEach(function (period) {
        result.push("".concat(period.period, ": Team average attended ").concat(period.teamAverageAttended, ", Team average skipped ").concat(period.teamAverageSkipped));
    });
    return result;
};
